// Data Manager - Centralized data management with persistence and error handling

import { DashboardStats, MinerStatus } from '../types';
import { miningServices } from './api';
import { persistenceService } from './persistence';

export interface DataState {
  dashboardStats: DashboardStats | null;
  miners: MinerStatus[];
  cryptoProgress: any;
  averageHashrate: any;
  lastUpdate: number;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  dataQuality: 'fresh' | 'cached' | 'stale' | 'missing';
}

export interface DataManagerConfig {
  onStateChange: (state: DataState) => void;
  onError: (error: string, context: string) => void;
  onSuccess: (message: string) => void;
  onWarning: (message: string) => void;
}

class DataManager {
  private state: DataState;
  private config: DataManagerConfig;
  private fetchTimeout: NodeJS.Timeout | null = null;
  private retryTimeout: NodeJS.Timeout | null = null;
  private autoRefreshInterval: NodeJS.Timeout | null = null;

  constructor(config: DataManagerConfig) {
    this.config = config;
    this.state = {
      dashboardStats: null,
      miners: [],
      cryptoProgress: null,
      averageHashrate: null,
      lastUpdate: 0,
      isLoading: true,
      isFetching: false,
      error: null,
      dataQuality: 'missing'
    };
  }

  // Initialize data manager
  async initialize(): Promise<void> {
    console.log('🚀 Initializing Data Manager...');
    
    // Load cached data immediately
    this.loadFromCache();
    
    // Determine data quality
    this.updateDataQuality();
    
    // Fetch fresh data if needed
    if (this.shouldFetchFreshData()) {
      await this.fetchAllData();
    } else {
      this.setState({ isLoading: false });
    }
    
    // Start auto-refresh
    this.startAutoRefresh();
  }

  // Load data from cache
  private loadFromCache(): void {
    console.log('📦 Loading data from cache...');
    
    const cachedStats = persistenceService.getDashboardStats();
    const cachedMiners = persistenceService.getMinersData();
    const cachedCrypto = persistenceService.getCryptoProgress();
    const cachedHashrate = persistenceService.getAverageHashrate();
    
    let hasData = false;
    
    if (cachedStats) {
      this.state.dashboardStats = cachedStats;
      hasData = true;
      console.log('✅ Loaded dashboard stats from cache');
    }
    
    if (cachedMiners && cachedMiners.length > 0) {
      this.state.miners = cachedMiners;
      hasData = true;
      console.log('✅ Loaded miners data from cache');
    }
    
    if (cachedCrypto) {
      this.state.cryptoProgress = cachedCrypto;
      hasData = true;
      console.log('✅ Loaded crypto progress from cache');
    }
    
    if (cachedHashrate) {
      this.state.averageHashrate = cachedHashrate;
      hasData = true;
      console.log('✅ Loaded hashrate data from cache');
    }
    
    if (hasData) {
      const fetchStatus = persistenceService.getFetchStatus();
      this.state.lastUpdate = fetchStatus.lastSuccessfulFetch;
      this.state.isLoading = false;
      this.notifyStateChange();
    }
  }

  // Determine if we should fetch fresh data
  private shouldFetchFreshData(): boolean {
    const fetchStatus = persistenceService.getFetchStatus();
    const now = Date.now();
    
    // Always fetch on first load
    if (fetchStatus.isFirstLoad) return true;
    
    // Fetch if data is older than 2 minutes
    if (now - fetchStatus.lastSuccessfulFetch > 120000) return true;
    
    // Fetch if we have no data
    if (!this.state.dashboardStats && !this.state.miners.length) return true;
    
    return false;
  }

  // Update data quality based on current state
  private updateDataQuality(): void {
    const fetchStatus = persistenceService.getFetchStatus();
    const now = Date.now();
    const dataAge = now - fetchStatus.lastSuccessfulFetch;
    
    if (!this.state.dashboardStats && !this.state.miners.length) {
      this.state.dataQuality = 'missing';
    } else if (dataAge < 60000) { // Less than 1 minute
      this.state.dataQuality = 'fresh';
    } else if (dataAge < 300000) { // Less than 5 minutes
      this.state.dataQuality = 'cached';
    } else {
      this.state.dataQuality = 'stale';
    }
  }

  // Fetch all data with error handling
  async fetchAllData(isRetry = false): Promise<boolean> {
    if (!persistenceService.shouldRetry() && isRetry) {
      console.log('🛑 Retry not allowed yet');
      return false;
    }

    console.log(`🔄 ${isRetry ? 'Retrying' : 'Starting'} data fetch...`);
    
    this.setState({ 
      isFetching: true, 
      error: null 
    });

    try {
      // Note: We keep existing data in state during fetch attempts

      let hasAnySuccess = false;
      let lastError: Error | null = null;

      // Fetch dashboard stats
      try {
        const stats = await miningServices.getDashboardStats();
        this.state.dashboardStats = stats;
        persistenceService.setDashboardStats(stats);
        hasAnySuccess = true;
        console.log('✅ Dashboard stats fetched successfully');
      } catch (error) {
        console.warn('⚠️ Dashboard stats fetch failed:', error);
        lastError = error as Error;
        // Keep existing data
      }

      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch miners data
      try {
        const miners = await miningServices.getAllWorkers();
        this.state.miners = miners;
        persistenceService.setMinersData(miners);
        hasAnySuccess = true;
        console.log('✅ Miners data fetched successfully');
      } catch (error) {
        console.warn('⚠️ Miners data fetch failed:', error);
        lastError = error as Error;
        // Keep existing data
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch crypto progress (optional)
      try {
        const crypto = await miningServices.getCryptoProgress();
        this.state.cryptoProgress = crypto;
        persistenceService.setCryptoProgress(crypto);
        hasAnySuccess = true;
        console.log('✅ Crypto progress fetched successfully');
      } catch (error) {
        console.warn('⚠️ Crypto progress fetch failed:', error);
        lastError = error as Error;
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch hashrate data (optional)
      try {
        const hashrate = await miningServices.getAverageHashrate();
        this.state.averageHashrate = hashrate;
        persistenceService.setAverageHashrate(hashrate);
        hasAnySuccess = true;
        console.log('✅ Hashrate data fetched successfully');
      } catch (error) {
        console.warn('⚠️ Hashrate data fetch failed:', error);
        lastError = error as Error;
      }

      if (hasAnySuccess) {
        // Mark as successful
        persistenceService.markFetchSuccess();
        
        this.setState({
          isLoading: false,
          isFetching: false,
          error: null,
          lastUpdate: Date.now()
        });

        this.updateDataQuality();
        
        if (isRetry) {
          this.config.onSuccess('Connection restored - data updated successfully');
        }

        console.log('✅ Data fetch completed successfully');
        return true;
      } else {
        throw lastError || new Error('All fetch attempts failed');
      }

    } catch (error) {
      console.error('❌ Complete fetch failure:', error);
      
      // Mark as failed
      persistenceService.markFetchFailure();
      const fetchStatus = persistenceService.getFetchStatus();
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch mining data';
      
      this.setState({
        isFetching: false,
        error: errorMessage,
        isLoading: false
      });

      this.updateDataQuality();

      // Handle error notifications
      if (fetchStatus.failedAttempts === 1) {
        if (this.state.dashboardStats || this.state.miners.length > 0) {
          this.config.onWarning('Connection issue - using cached data while retrying');
        } else {
          this.config.onError('Unable to fetch data', 'No cached data available');
        }
      } else if (fetchStatus.failedAttempts >= 3) {
        this.config.onError('Connection failed after multiple attempts', 'Using cached data only');
      }

      // Schedule retry
      if (fetchStatus.failedAttempts < 3) {
        const retryDelay = persistenceService.calculateRetryDelay(fetchStatus.failedAttempts);
        console.log(`⏰ Scheduling retry in ${retryDelay}ms`);
        
        this.retryTimeout = setTimeout(() => {
          this.fetchAllData(true);
        }, retryDelay);
      }

      return false;
    }
  }

  // Manual refresh
  async refresh(): Promise<boolean> {
    // Clear any pending retries
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    // Reset fetch status
    persistenceService.setFetchStatus({
      failedAttempts: 0,
      nextRetryTime: 0
    });
    
    return await this.fetchAllData(false);
  }

  // Start auto-refresh
  private startAutoRefresh(): void {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }

    this.autoRefreshInterval = setInterval(() => {
      if (persistenceService.needsRefresh() && persistenceService.shouldRetry()) {
        console.log('🔄 Auto-refresh triggered');
        this.fetchAllData(false);
      }
    }, 30000); // Check every 30 seconds

    console.log('⏰ Auto-refresh started');
  }

  // Stop auto-refresh
  stopAutoRefresh(): void {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;
      console.log('🛑 Auto-refresh stopped');
    }
  }

  // Update state and notify
  private setState(updates: Partial<DataState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyStateChange();
  }

  // Notify state change
  private notifyStateChange(): void {
    this.config.onStateChange({ ...this.state });
  }

  // Get current state
  getState(): DataState {
    return { ...this.state };
  }

  // Get data freshness info
  getDataFreshness() {
    return persistenceService.getDataFreshness();
  }

  // Check if can retry
  canRetry(): boolean {
    return persistenceService.shouldRetry();
  }

  // Get time until next retry
  getTimeUntilRetry(): number {
    const fetchStatus = persistenceService.getFetchStatus();
    return Math.max(0, fetchStatus.nextRetryTime - Date.now());
  }

  // Cleanup
  destroy(): void {
    if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
    if (this.retryTimeout) clearTimeout(this.retryTimeout);
    this.stopAutoRefresh();
    console.log('🧹 Data Manager destroyed');
  }
}

export default DataManager;